import { useMemo } from "react";
import { Permission } from "../../common/constants/permissions";
import { DUMMY_APPS } from "../../constants/mockData/catalogueApps";
import { IApp } from "../../types/app";

interface IDirectAppAccessRule {
  canAccess?: (roles: string[]) => boolean;
  permission: Permission;
}

interface IUseDirectAppAccessParams {
  accessRules: IDirectAppAccessRule[];
  roles?: string[];
  routeAppId?: string;
  search: string;
}

interface IDirectAppUrlResolution {
  iframeUrl?: string;
  isDirectDeepLink: boolean;
}

const MAX_PATH_DECODE_DEPTH = 5;

const normalizePath = ({ pathname }: { pathname: string }) => {
  const normalizedPath = pathname.replace(/\/+$/, "");

  return normalizedPath || "/";
};

const decodePathToStableValue = ({ pathname }: { pathname: string }) => {
  let decodedPathname = pathname;

  // URLSearchParams decodes the nested url once before this hook sees it.
  // Decode the pathname to a stable value so repeatedly encoded traversal
  // markers, such as `%252e%252e%252f`, become real `/../` segments before
  // URL normalization and cannot pass the launch-path boundary check.
  for (let decodeDepth = 0; decodeDepth < MAX_PATH_DECODE_DEPTH; decodeDepth += 1) {
    const nextDecodedPathname = decodeURIComponent(decodedPathname);

    if (nextDecodedPathname === decodedPathname) {
      return decodedPathname;
    }

    decodedPathname = nextDecodedPathname;
  }

  return undefined;
};

const normalizeDecodedPath = ({ pathname }: { pathname: string }) => {
  const decodedPathname = decodePathToStableValue({ pathname });

  if (!decodedPathname) {
    return undefined;
  }

  return new URL(decodedPathname, "https://frontdoor.findem.ai").pathname;
};

const getLaunchUrlParts = ({ launchUrl }: { launchUrl: string }) => {
  try {
    const url = new URL(launchUrl);

    return {
      origin: url.origin,
      pathname: normalizePath({ pathname: url.pathname }),
    };
  } catch {
    return undefined;
  }
};

const isPathWithinLaunchPath = ({
  launchPath,
  nestedPath,
}: {
  launchPath: string;
  nestedPath: string;
}) => {
  const normalizedLaunchPath = normalizePath({ pathname: launchPath });
  const normalizedNestedPath = normalizePath({ pathname: nestedPath });

  return (
    normalizedNestedPath === normalizedLaunchPath ||
    normalizedNestedPath.startsWith(`${normalizedLaunchPath}/`)
  );
};

const getDirectAppIframeUrl = ({
  launchUrl,
  search,
}: {
  launchUrl: string;
  search: string;
}): IDirectAppUrlResolution => {
  const launchUrlParts = getLaunchUrlParts({ launchUrl });
  const nestedUrl = new URLSearchParams(search).get("url");

  if (!nestedUrl) {
    return { isDirectDeepLink: true };
  }

  if (!launchUrlParts || !nestedUrl.startsWith("/") || nestedUrl.startsWith("//")) {
    return { isDirectDeepLink: true };
  }

  try {
    const normalizedNestedUrl = new URL(nestedUrl, launchUrlParts.origin);
    const normalizedNestedPath = normalizeDecodedPath({
      pathname: normalizedNestedUrl.pathname,
    });

    if (
      !normalizedNestedPath ||
      !isPathWithinLaunchPath({
        launchPath: launchUrlParts.pathname,
        nestedPath: normalizedNestedPath,
      })
    ) {
      return { isDirectDeepLink: true };
    }

    return {
      iframeUrl: `${launchUrlParts.origin}${normalizedNestedUrl.pathname}${normalizedNestedUrl.search}${normalizedNestedUrl.hash}`,
      isDirectDeepLink: true,
    };
  } catch {
    return { isDirectDeepLink: true };
  }
};

/**
 * Resolves iframe access for direct app deep links that are allowed outside the
 * normal installed-app catalog result.
 *
 * The hook intentionally starts from accessRules instead of the current route.
 * A rule opts one app permission into this alternate path; every app not listed
 * here keeps the existing behavior, even when routeAppId and url match a
 * catalog entry. This prevents an ACL exception for Pathways from accidentally
 * enabling direct access to People Lists or future apps.
 *
 * Example: Pathways can stay out of the installed apps list for users without
 * Permission.PathwaysApp, while a RAAS People List link can still open a
 * Pathways deep link when the configured rule allows FM Agent users.
 *
 * For a configured app, access is granted when the user has the app's own
 * permission or the rule's canAccess predicate returns true. Deep-link matching
 * uses the configured launchUrl path, so callers do not need to hardcode app
 * ids or path prefixes.
 */
export const useDirectAppAccess = ({
  accessRules,
  roles = [],
  routeAppId,
  search,
}: IUseDirectAppAccessParams) => {
  const directAccessApp = useMemo(() => {
    for (const rule of accessRules) {
      const app = DUMMY_APPS.find(
        (app: IApp) =>
          app.id === routeAppId && app.permission === rule.permission,
      );

      if (app) {
        return { app, rule };
      }
    }

    return undefined;
  }, [accessRules, routeAppId]);
  const { app, rule } = directAccessApp ?? {};
  const directAppUrlResolution = app
    ? getDirectAppIframeUrl({ launchUrl: app.launchUrl, search })
    : { isDirectDeepLink: false };
  const isDirectDeepLink = Boolean(rule && directAppUrlResolution.isDirectDeepLink);
  const hasAccess =
    isDirectDeepLink &&
    Boolean(
      app?.permission &&
        directAppUrlResolution.iframeUrl &&
        (roles.includes(app.permission) || rule?.canAccess?.(roles)),
    );

  return {
    iframeUrl: hasAccess ? directAppUrlResolution.iframeUrl : undefined,
    shouldShowAccessDenied: isDirectDeepLink && !hasAccess,
  };
};
